import footerContent from 'platform/forms/components/FormFooter';
import { VA_FORM_IDS } from 'platform/forms/constants';
import { arrayBuilderPages } from '~/platform/forms-system/src/js/patterns/array-builder';
import { TITLE, SUBTITLE, SUBMIT_URL } from '../constants';

import manifest from '../manifest.json';
import submitForm from './submitForm';
import { transform } from './submit-transformer';
import prefillTransformer from './prefill-transformer';

// Components
import IntroductionPage from '../containers/IntroductionPage';
import ConfirmationPage from '../containers/ConfirmationPage';
import PreSubmitInfo from '../components/PreSubmitInfo';
import PhoneAndEmailPage from '../components/PhoneAndEmailPage';
import NeedHelp from '../components/NeedHelp';
import applicantFullnameReviewPage from '../components/ApplicantFullnameReviewPage';

// Pages
import {
  applicantFullname,
  mailingAddress,
  phoneAndEmail,
  employmentStatus,
  employmentDetails,
  employmentFocus,
  salaryDetails,
  educationDetails,
  trainingProviderSummary,
  trainingProviderDetails,
  trainingProviderStartDate,
  atLeast3Years,
  directDeposit,
  contactPreferences,
} from '../pages';

import { trainingProviderArrayOptions, focusOnH3 } from '../helpers';

import dateReleasedFromActiveDuty from '../pages/dateReleasedFromActiveDuty';
import activeDutyStatus from '../pages/activeDutyStatus';
import contactMethod from '../pages/contactMethod';
import educationLetters from '../pages/educationLetters';
import monthlyEnrollment from '../pages/monthlyEnrollment';
import { normalizeReturnUrl } from '../utils/normalizeReturnUrl';

export const submitFormLogic = (form, formConfig) => {
  return submitForm(form, formConfig);
};

/** @type {FormConfig} */
const formConfig = {
  rootUrl: manifest.rootUrl,
  urlPrefix: '/',
  submitUrl: SUBMIT_URL,
  submit: submitFormLogic,
  normalizeReturnUrl,
  trackingPrefix: 'edu-10297',
  introduction: IntroductionPage,
  confirmation: ConfirmationPage,
  formId: VA_FORM_IDS.FORM_22_10297,
  saveInProgress: {
    messages: {
      inProgress: 'Your form (22-10297) is in progress.',
      expired:
        'Your saved form (22-10297) has expired. Please start a new form.',
      saved: 'Your form has been saved.',
    },
  },
  version: 0,
  prefillEnabled: true,
  prefillTransformer,
  savedFormMessages: {
    notFound: 'Please start over.',
    noAuth: 'Please sign in again to continue your form.',
  },
  title: TITLE,
  subTitle: SUBTITLE,
  customText: {
    appType: 'form',
    continueAppButtonText: 'Continue your form',
    startNewAppButtonText: 'Start a new form',
    finishAppLaterMessage: 'Finish this form later',
    appSavedSuccessfullyMessage: 'We’ve saved your form.',
  },
  defaultDefinitions: {},
  preSubmitInfo: {
    CustomComponent: PreSubmitInfo,
    required: true,
    field: 'privacyAgreementAccepted',
    statementOfTruth: {
      // heading: 'Certification statement',
      // // body: PreSubmitInfo,
      messageAriaDescribedby: 'I have read and accept the privacy policy.',
      fullNamePath: 'applicantFullName',
      useProfileFullName: true,
    },
  },
  transformForSubmit: transform,
  useCustomScrollAndFocus: true,
  chapters: {
    identificationChapter: {
      title: 'Your information',
      pages: {
        applicantFullName: {
          path: 'applicant-fullname',
          title: 'Name and date of birth',
          uiSchema: applicantFullname.uiSchema,
          schema: applicantFullname.schema,
          CustomPageReview: applicantFullnameReviewPage,
        },
        mailingAddress: {
          path: 'mailing-address',
          title: 'Mailing address',
          uiSchema: mailingAddress.uiSchema,
          schema: mailingAddress.schema,
          pageClass: 'page',
        },
        phoneAndEmail: {
          depends: formData => !formData?.mebNewContactInfoUrls,
          path: 'phone-and-email',
          title: 'Phone and email address',
          CustomPage: PhoneAndEmailPage,
          CustomPageReview: null,
          uiSchema: phoneAndEmail.uiSchema,
          schema: phoneAndEmail.schema,
          pageClass: 'page',
          updateFormData: phoneAndEmail.updateFormData,
        },
        phoneAndEmailV2: {
          depends: formData => formData?.mebNewContactInfoUrls,
          path: 'phone-email',
          title: 'Phone and email address',
          CustomPage: PhoneAndEmailPage,
          CustomPageReview: null,
          uiSchema: phoneAndEmail.uiSchema,
          schema: phoneAndEmail.schema,
          pageClass: 'page',
          updateFormData: phoneAndEmail.updateFormData,
        },
        contactPreferences: {
          depends: formData => !formData?.mebDole210Updates,
          path: 'contact-preferences',
          title: 'Contact preferences',
          uiSchema: contactPreferences.uiSchema,
          schema: contactPreferences.schema,
          pageClass: 'page',
        },
        contactMethod: {
          depends: formData => formData?.mebDole210Updates,
          title: 'Choose your contact method for follow-up questions',
          path: 'contact-method',
          uiSchema: contactMethod.uiSchema,
          schema: contactMethod.schema,
        },
        educationLetters: {
          depends: formData => formData?.mebDole210Updates,
          title: 'Select your notification preferences for education letters',
          path: 'education-letters',
          uiSchema: educationLetters.uiSchema,
          schema: educationLetters.schema,
        },
        monthlyEnrollment: {
          depends: formData => formData?.mebDole210Updates,
          title: 'Choose how you want to verify your monthly enrollment',
          path: 'monthly-enrollment',
          uiSchema: monthlyEnrollment.uiSchema,
          schema: monthlyEnrollment.schema,
        },
        dutyRequirementMet: {
          path: 'at-least-3-years',
          title: 'Your Veteran or service member status',
          uiSchema: atLeast3Years.uiSchema,
          schema: atLeast3Years.schema,
          pageClass: 'page',
        },
        dateReleasedFromActiveDuty: {
          path: 'date-released-from-active-duty',
          title: 'Active duty status release date',
          uiSchema: dateReleasedFromActiveDuty.uiSchema,
          schema: dateReleasedFromActiveDuty.schema,
          pageClass: 'page',
        },
        activeDutyStatus: {
          path: 'active-duty-status',
          title: 'Active duty status during program',
          uiSchema: activeDutyStatus.uiSchema,
          schema: activeDutyStatus.schema,
          pageClass: 'page',
        },
        directDeposit: {
          path: 'direct-deposit',
          title: 'Direct deposit',
          uiSchema: directDeposit.uiSchema,
          schema: directDeposit.schema,
          pageClass: 'page',
        },
      },
    },
    trainingProviderChapter: {
      title: 'Training provider details',
      pages: {
        ...arrayBuilderPages(trainingProviderArrayOptions, pageBuilder => ({
          trainingProviderSummary: pageBuilder.summaryPage({
            title: 'Tell us about your training provider',
            path: 'training-provider',
            uiSchema: trainingProviderSummary.uiSchema,
            schema: trainingProviderSummary.schema,
            pageClass: 'page training-provider-summary',
            scrollAndFocusTarget: focusOnH3,
          }),
          trainingProviderDetails: pageBuilder.itemPage({
            title: 'Training provider name and mailing address',
            path: 'training-provider/:index/details',
            uiSchema: trainingProviderDetails.uiSchema,
            schema: trainingProviderDetails.schema,
            pageClass: 'page',
          }),
        })),
        trainingProviderStartDate: {
          path: 'training-provider-start-date',
          title:
            'Do you have a start date for the program you wish to enroll in?',
          uiSchema: trainingProviderStartDate.uiSchema,
          schema: trainingProviderStartDate.schema,
          pageClass: 'page',
        },
      },
    },
    backgroundInformationChapter: {
      title: 'Background information',
      pages: {
        employmentStatus: {
          path: 'employment-status',
          title: 'Your employment',
          uiSchema: employmentStatus.uiSchema,
          schema: employmentStatus.schema,
          pageClass: 'page',
          updateFormData: (_oldFormData, newFormData) => {
            return {
              ...newFormData,
              isInTechnologyIndustry: undefined,
              technologyAreaOfFocus: undefined,
              currentSalary: undefined,
            };
          },
        },
        employmentDetails: {
          path: 'employment-details',
          title: 'Your technology industry involvement',
          uiSchema: employmentDetails.uiSchema,
          schema: employmentDetails.schema,
          pageClass: 'page',
          depends: formData => formData.isEmployed === true,
          updateFormData: (_oldFormData, newFormData) => {
            return {
              ...newFormData,
              technologyAreaOfFocus: undefined,
            };
          },
        },
        employmentFocus: {
          path: 'employment-focus',
          title: 'Your main area of focus',
          uiSchema: employmentFocus.uiSchema,
          schema: employmentFocus.schema,
          pageClass: 'page',
          depends: formData =>
            formData.isEmployed === true &&
            formData.isInTechnologyIndustry === true,
        },
        salaryDetails: {
          path: 'salary-details',
          title: 'Your current annual salary',
          uiSchema: salaryDetails.uiSchema,
          schema: salaryDetails.schema,
          pageClass: 'page',
          depends: formData => formData.isEmployed === true,
        },
        educationDetails: {
          path: 'education-details',
          title: 'Your education',
          uiSchema: educationDetails.uiSchema,
          schema: educationDetails.schema,
          pageClass: 'page',
        },
      },
    },
  },
  getHelp: NeedHelp,
  footerContent,
};

export default formConfig;
